// English — reference set. Every other locale mirrors these keys.
// Keys are grouped by domain (auth, dashboard, wallet, topup, cash, profile,
// commissions, common). New pages MUST add their keys under a dedicated
// namespace and mirror them in es.js + fr.js to keep the app fully
// translated.
const en = {
  translation: {
    // Sidebar
    nav: {
      profile: "Profile",
      dashboard: "Dashboard",
      analytics: "Analytics",
      wallet: "My Wallet",
      send_topup: "Send Topup",
      gift_cards: "Gift Cards",
      pay_bills: "Utilities & Bills",
      agents: "My Agents",
      subagent_tx: "Subagent Transactions",
      master_agents: "Master Agents",
      set_commissions: "Set Commissions",
      my_commissions: "My Commissions",
      master_commissions: "Master Commissions",
      master_payouts: "Payouts",
      transfer_funds: "Transfer Funds",
      reports: "Reports",
      settings: "Local Currency",
      make_payment: "Make Payment",
      send_cash: "Send Cash",
      cash_verification: "Cash Verification",
      exchange_rates: "Rates & Fees",
      branding_settings: "Branding Settings",
      pending_codes: "Pending Codes",
      sms_diagnostics: "SMS Diagnostics",
      provider_management: "Provider Management",
      pending_password_reset_codes: "Pending Password Reset Codes",
      trusted_devices: "Trusted Devices",
      audit_log: "Audit Log",
      my_activity: "My Activity",
      section_navigation: "Navigation",
      folder_my_agents: "My Agents",
      folder_master_agents: "Master Agents",
      logout: "Logout",
      folder_create_agent: "Create Agent",
      folder_agent_management: "Agent Management",
      folder_create_subagent: "Create Subagent",
      folder_management: "Management",
      folder_subagent_tx: "Subagent Transactions",
      folder_subagent_sales: "Subagent Sales",
      folder_gross_sales: "Gross Sales Report",
      folder_set_agent_commissions: "Set Commissions",
      folder_network_performance: "Network Performance",
      folder_create_master: "Create Master Agent",
      folder_master_list: "Master Agents List",
      folder_master_commissions: "Master Agent Commissions",
      folder_set_master_commissions: "Set Master Commissions",
      folder_master_sales: "Master Agent Sales",
      folder_master_tx: "Master Agent Transactions",
      folder_commission_reports: "Commission Reports",
      folder_collaborators: "Collaborators",
      folder_collaborator_management: "Collaborator Management",
      folder_collaborator_create: "Create Collaborator",
      folder_collaborator_activity: "Collaborator Activity",
      folder_collaborator_sessions: "Collaborator Sessions",
      folder_collaborator_approvals: "Approval Requests",
      folder_compliance: "Compliance Dashboard",
      folder_my_wallet: "My Wallet",
      folder_currencies: "Currencies",
      folder_send_external_transfer: "Send External Transfer",
      folder_funding_history: "Funding History",
      signed_in_as: "Signed in as",
      sign_out: "Sign out",
    },
    role: {
      MASTER_ADMIN: "Master Admin",
      MASTER_AGENT: "Master Agent",
      AGENT: "Agent",
      CASH_VERIFIER: "Cash Verifier",
      COLLABORATOR: "Collaborator",
    },
    // Auth pages
    auth: {
      brand_tagline: "Global mobile topup reseller platform",
      sign_in_title: "Sign in to your account",
      sign_in_sub: "Enter your credentials to continue.",
      welcome_back: "Welcome Back!",
      username_placeholder: "Enter your username",
      password_placeholder: "Enter your password",
      username: "Username",
      username_or_email: "Username or email",
      password: "Password",
      remember_me: "Remember me",
      sign_in: "Sign in",
      signing_in: "Signing in…",
      forgot_password: "Forgot password?",
      new_here: "New here?",
      create_account: "Create an account",
      have_account: "Already have an account?",
      register_title: "Create your account",
      register_sub: "Register a new reseller account.",
      name: "Name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      confirm_password: "Confirm password",
      passwords_dont_match: "Passwords do not match.",
      register: "Register",
      registering: "Registering…",
      forgot_title: "Forgot your password?",
      forgot_sub: "Enter your email and we'll send you a reset link.",
      send_reset_link: "Send reset link",
      sending: "Sending…",
      back_to_login: "Back to sign in",
      back_to_landing: "Back to Landing Page",
      reset_title: "Reset your password",
      reset_sub: "Choose a new password for your account.",
      new_password: "New password",
      reset_password: "Reset password",
      resetting: "Resetting…",
      reset_success: "Password reset. You can now sign in with your new password.",
      email_sent: "Check your inbox for reset instructions.",
      two_factor_title: "Enter verification code",
      two_factor_sub: "We sent a 6-digit code to {{email}}. It expires in {{minutes}} minutes.",
      code: "Verification code",
      verify: "Verify",
      verifying: "Verifying…",
      resend_code: "Resend code",
      resend_in: "Resend in {{seconds}}s",
      trust_device: "Trust this device (skip SMS on next login)",
      device_name: "Device name (optional)",
      cancel: "Cancel",
      pw_rule_length: "At least 8 characters",
      pw_rule_upper: "One uppercase letter (A-Z)",
      pw_rule_lower: "One lowercase letter (a-z)",
      pw_rule_digit: "One number (0-9)",
      pw_rule_special: "One special character (!@#$…)",
    },
    pwreset: {
      title: "Forgot your password?",
      step1_sub: "Enter your username, email, or registered phone number. We'll SMS a 6-digit code to your registered phone.",
      step1_sub_v2: "Enter your registered email or your registered mobile phone number. We'll send a 6-digit code via SMS to the phone registered on your account.",
      identifier_label: "Username, Email or Phone (E.164)",
      identifier_label_v2: "Email or mobile phone number",
      identifier_placeholder_v2: "Enter your registered email or your registered mobile phone number",
      identifier_examples: "Examples: john@example.com  ·  +56961234567",
      identifier_invalid: "Enter a valid email (name@example.com) or a phone in E.164 format (e.g. +56961234567).",
      send_sms: "Send verification code",
      step2_title: "Enter the SMS code",
      step2_sub_sent: "A 6-digit code was sent to",
      step2_sub_expiry: "It expires in 5 minutes.",
      step2_sub_generic: "If that account exists, a 6-digit code has been sent to the registered phone number. It expires in 5 minutes.",
      step2_generic_v2: "If the information you entered is valid, a verification code has been sent. It expires in 5 minutes.",
      step2_nomatch_hint: "Not receiving anything? Double-check your username, email, or phone (E.164 format like +56961000199). If your account is registered without a phone number, contact support to reset your password.",
      code_label: "6-digit code",
      resend_in: "Resend in {{s}}s",
      resend_sms: "Resend SMS",
      change_identifier: "Use a different account",
      change_identifier_v2: "Use a different email or phone",
      verify: "Verify code",
      invalid_code_generic: "The code you entered is not valid or has expired.",
      step3_title: "Create a new password",
      step3_sub: "Password must have at least 8 characters, an uppercase and lowercase letter, a digit and a special character.",
      step3_sub_v2: "Choose a strong password. All requirements below must be met.",
      new_password: "New password",
      confirm_password: "Confirm new password",
      pw_rule_length: "At least 8 characters",
      pw_rule_letter: "At least one letter",
      pw_rule_digit: "At least one number",
      pw_rule_no_edge_space: "No spaces at the start or end",
      updating: "Updating…",
      update_password: "Update password",
      reset_password: "Reset password",
      mismatch: "Passwords do not match.",
      resent: "New SMS sent ({{used}}/{{max}})",
      done_title: "Password updated",
      done_title_v2: "Password updated successfully",
      done_sub: "All active sessions and trusted devices for this account have been revoked. Sign in with your new password — a new SMS code will be required for this device.",
      done_sub_v2: "Your password has been updated successfully.",
      back_to_login: "Back to sign in",
      step_identify: "Identify",
      step_verify: "Verify",
      step_new: "New password",
    },
    // Dashboard
    dashboard: {
      title: "Dashboard",
      welcome: "Welcome back",
      today: "Today",
      week: "This week",
      month: "This month",
      custom: "Custom range",
      range_yesterday: "Yesterday",
      range_7d: "Last 7 Days",
      range_30d: "Last 30 Days",
      range_last_month: "Last Month",
      range_from: "From",
      range_to: "To",
      kpi_available_balance: "Available balance",
      kpi_total_sales: "Total sales",
      kpi_my_commissions: "My commissions",
      kpi_total_transactions: "Total transactions",
      kpi_master_agents: "Master agents",
      kpi_total_agents: "Total agents",
      kpi_network_volume: "Network volume",
      kpi_top_performer: "Top performer",
      internal_pool: "Internal pool",
      lifetime: "Lifetime",
      sales_by_master: "Sales by master agent",
      sales_by_agent: "Sales by sub-agent",
      col_id: "ID",
      col_created: "Created",
      col_product: "Product",
      col_phone: "Phone",
      col_amount_paid: "Amount paid",
      col_customer_receives: "Customer receives",
      master_wallet_balance: "Master Wallet Balance",
      master_wallet_hint: "Platform wallet used to fund the entire network",
      last_updated: "last updated",
      add_funds: "Add Funds",
      low_balance_warning: "Low balance — below {{threshold}} threshold. Consider adding funds.",
      transactions: "Transactions",
      funding_history: "Funding history",
      recent_transactions: "Recent transactions",
      quick_actions: "Quick actions",
      view_all: "View all",
      no_transactions: "No transactions yet.",
      pending_topups: "Pending",
      successful_topups: "Successful topups",
      failed_topups: "Failed topups",
      sales_today: "Sales today",
    },
    // Wallet page
    wallet: {
      title: "My Wallet",
      subtitle: "Track your balance, ledger entries and funding history.",
      current_balance: "Current balance",
      available: "Available",
      currency_usd: "USD",
      request_funds: "Request funds",
      add_funds: "Add funds",
      export: "Export",
      transactions_tab: "Ledger transactions",
      funding_tab: "Funding history",
      col_date: "Date",
      col_type: "Type",
      col_source: "Category",
      col_reference: "Reference",
      col_amount: "Amount",
      col_balance_after: "Balance after",
      col_notes: "Note",
      col_status: "Status",
      no_entries: "No ledger entries yet.",
      no_funding: "No funding requests yet.",
      loading: "Loading wallet…",
      status_pending: "Pending",
      status_approved: "Approved",
      status_rejected: "Rejected",
      type_credit: "CREDIT",
      type_debit: "DEBIT",
      cat_topup_hold: "Provider Topup",
      cat_topup_reversal: "Topup Reversal",
      cat_topup_trueup: "Topup True-up",
      cat_commission: "Commission",
      cat_master_commission_payout: "Master Agent Weekly Commission",
      cat_master_commission_reversal: "Master Agent Commission Reversal",
      cat_transfer_in: "Transfer In",
      cat_transfer_out: "Transfer Out",
      cat_funding: "Funding",
      cat_funding_payku: "Payku Funding",
      cat_funding_manual: "Manual Funding",
      cat_funding_card: "Card Funding",
    },
    // Send Topup
    topup: {
      title: "Send Topup",
      subtitle: "Send mobile phone credit to any supported operator worldwide.",
      country: "Country",
      operator: "Operator",
      product: "Product",
      phone: "Phone number",
      select_country: "Select country",
      select_operator: "Select operator",
      select_product: "Select product",
      phone_placeholder: "Enter recipient phone number",
      estimate: "Estimate",
      estimating: "Estimating…",
      send: "Send Topup",
      sending: "Sending…",
      cost_usd: "Cost (USD)",
      amount_to_send: "Amount to send",
      recipient_receives: "Recipient receives",
      commission: "Your commission",
      confirm_title: "Confirm topup",
      confirm_sub: "Please review the details before sending.",
      confirm_send: "Confirm & Send",
      success_title: "Topup sent!",
      success_sub: "Transaction #{{id}} completed successfully.",
      failed_title: "Topup failed",
      failed_sub: "The transaction could not be completed. Your balance was refunded.",
      no_products: "No products available for the selected operator.",
      insufficient_balance: "Insufficient wallet balance for this topup.",
      recent: "Recent topups",
      view_receipt: "View receipt",
      print: "Print",
      status_success: "Success",
      status_pending: "Pending",
      status_failed: "Failed",
      status_cancelled: "Cancelled",
      status_hold: "On hold",
    },
    // Send Cash
    cash: {
      title: "Send Cash",
      subtitle: "Send cash transfers to recipients — pickup at agent locations.",
      failed_credit: "Failed to credit wallet",
      failed_send: "Failed to send cash",
      provide_reject_reason: "Please provide a rejection reason",
      provide_verifier_account: "Please enter the verifier account that confirmed this transaction",
      recipient_name: "Recipient name",
      recipient_last_name: "Recipient last name",
      recipient_phone: "Recipient phone",
      recipient_country: "Recipient country",
      recipient_city: "Recipient city",
      amount_usd: "Amount (USD)",
      note: "Note (optional)",
      review: "Review",
      send_cash: "Send Cash",
      sending: "Sending…",
      confirm_title: "Confirm cash transfer",
      confirm_sub: "Please review the details before sending.",
      confirm_send: "Confirm & Send",
      success_title: "Cash transfer created",
      success_sub: "Pickup code: {{code}}",
      insufficient_balance: "Insufficient wallet balance.",
      recent: "Recent transfers",
      col_recipient: "Recipient",
      col_amount: "Amount",
      col_status: "Status",
      col_created: "Created",
      col_pickup_code: "Pickup code",
      status_pending: "Pending",
      status_completed: "Completed",
      status_cancelled: "Cancelled",
    },
    // Profile page
    profile: {
      title: "Profile",
      subtitle: "Manage your personal information, security, and preferences.",
      tab_personal: "Personal Information",
      tab_password: "Password",
      tab_preferences: "Preferences",
      tab_billing: "Billing Information",
      account_number: "Account number",
      name: "Name",
      last_name: "Last name",
      email: "Email",
      phone: "Phone",
      state: "State / Region",
      city: "City",
      address: "Address",
      save: "Save changes",
      saving: "Saving…",
      saved: "Profile saved",
      current_password: "Current password",
      new_password: "New password",
      confirm_password: "Confirm new password",
      change_password: "Change password",
      changing_password: "Changing password…",
      password_changed: "Password changed successfully.",
      passwords_dont_match: "Passwords do not match.",
    },
    // Settings (Local Currency)
    settings: {
      title: "Local currency",
      subtitle: "Configure how amounts are displayed to you and your customers.",
      currency: "Currency",
      exchange_rate: "Exchange rate (1 USD =)",
      save: "Save",
      saved: "Settings saved",
      preferences: "Preferences",
      language: "Interface language",
      language_hint: "Changes apply immediately across the app.",
    },
    // Set Commissions
    commissions: {
      title: "Set Commissions",
      title_master: "Set Master Agent Commissions",
      title_agent: "Set Agent Commissions",
      subtitle: "Manually set the commission percentage each agent earns per operator. Applies to every recharge or cash operation for that operator.",
      subtitle_master: "Configure per-operator commission percentages for Master Agents. Agents are not shown here.",
      subtitle_agent: "Configure per-operator commission percentages for Agents. Master Agents are not shown here.",
      col_master_commission: "Master Commission",
      col_agent_commission: "Agent Commission (%)",
      you_keep: "You keep {{pct}}%",
      pick_agent: "Select an agent",
      no_agents: "You have no agents to configure yet.",
      search_products: "Search products…",
      country: "Country",
      operator: "Operator",
      all: "All",
      col_product: "Product",
      col_ding_pct: "Ding %",
      col_parent_pct: "Available to you",
      col_allocated_pct: "Assigned",
      col_remaining_pct: "Remaining",
      col_shared_pct: "Already shared",
      save: "Save",
      saved: "Commission updated",
      invalid_over: "Cannot exceed the available %",
      invalid_under: "Cannot drop below already-shared %",
      empty: "No products match your filters.",
      hierarchy_master: "Master Admin allocates from Ding, you allocate from what you receive.",
      hierarchy_admin: "You allocate to Master Agents. They then distribute to their own Agents.",
      showing: "Showing {{count}} products",
    },
    // Transfer Funds
    transfer: {
      title: "Transfer Funds",
      subtitle: "Move funds between your master wallet and your agents.",
      master_balance: "Master balance",
      available_to_transfer: "Available to transfer",
      select_recipient: "Select recipient",
      recent_title: "Recent transfers",
      audit_hint: "Audit trail of all fund movements",
      withdraw_out: "Withdraw out",
      withdraw_in: "Withdraw in",
    },
    // Agent Management
    agents: {
      title: "Agent Management",
      subtitle: "Create, edit and manage the agents in your network.",
      create: "Create Agent",
      edit: "Edit Agent",
      col_username: "Username",
      col_name: "Name",
      col_email: "Email",
      col_phone: "Phone",
      col_balance: "Balance",
      col_status: "Status",
      col_commission: "Commission %",
      col_created: "Created",
      col_actions: "Actions",
      active: "Active",
      inactive: "Inactive",
      activate: "Activate",
      deactivate: "Deactivate",
      no_agents: "No agents yet. Create your first one to get started.",
      created: "Agent created",
      updated: "Agent updated",
      deactivated: "Agent deactivated",
      activated: "Agent activated",
    },
    // Master Agents list
    masters: {
      title: "Master Agents",
      subtitle: "Top-tier resellers in your network.",
      create: "Create Master Agent",
      col_username: "Username",
      col_name: "Name",
      col_email: "Email",
      col_phone: "Phone",
      col_balance: "Balance",
      col_agents: "Agents",
      col_status: "Status",
      col_created: "Created",
      col_actions: "Actions",
      no_masters: "No master agents yet.",
      created: "Master agent created",
      updated: "Master agent updated",
    },
    // Subagent Transactions
    sub_tx: {
      title: "Subagent Transactions",
      subtitle: "Every recharge sent by agents in your network.",
      col_agent: "Agent",
      col_product: "Product",
      col_phone: "Phone",
      col_amount: "Amount",
      col_status: "Status",
      col_created: "Created",
      no_tx: "No transactions in the selected range.",
    },
    // Analytics
    analytics: {
      title: "Analytics",
      subtitle: "Trends, top performers and network health.",
      kpi_period_sales: "Period sales",
      kpi_period_topups: "Period topups",
      kpi_avg_ticket: "Average ticket",
      kpi_success_rate: "Success rate",
      top_agents: "Top agents",
      top_operators: "Top operators",
      top_countries: "Top countries",
      no_data: "No data for the selected period.",
      crunching: "Crunching numbers…",
      export_failed: "Export failed",
      kpi_total_revenue: "Total revenue",
      kpi_send_topups: "Send Topups",
      kpi_send_cash: "Send Cash",
      kpi_wallet_funded: "Wallet funded",
      kpi_conv_fees: "Conv. fees",
      agent_profit_margin: "Agent profit margin",
      kpi_total_agents: "Total agents",
      kpi_master_agents: "Master agents",
      kpi_active_users: "Active users",
      inactive: "inactive",
      kpi_pending_cash: "Pending cash",
      kpi_completed: "Completed",
      kpi_failed: "Failed",
      financial_stats: "Financial statistics",
      total_profit: "Total profit",
      total_commission: "Total commission",
      convenience_fees: "Convenience fees",
      topup_wallet_balance: "Topup wallet balance",
      money_sent_topup: "Money sent (Topup)",
      money_sent_cash: "Money sent (Cash)",
      cash_balances: "Cash balances",
      chart_sales_by_service: "Sales by service",
      chart_topup_vs_cash: "Send Topup vs Send Cash",
      chart_daily_revenue: "Daily revenue",
      chart_daily_sub: "Topup + Send Cash (USD equivalents)",
      chart_top_countries: "Top countries",
      chart_top_countries_sub: "Sales by destination (Send Topup)",
      chart_sales_by_provider: "Sales by provider",
      chart_sales_by_provider_sub: "Send Topup providers + MonCash/NatCash",
      chart_cash_by_ccy: "Send Cash by currency",
      chart_cash_by_ccy_sub: "Base amount + HTG received",
      chart_funding_by_method: "Funding by payment method",
      chart_funding_sub: "Wallet top-ups (SUCCESS)",
      chart_top_10_agents: "Top 10 agents",
      chart_top_agents_sub: "Best sellers by revenue (USD)",
      chart_top_10_masters: "Top 10 master agents",
      chart_top_masters_sub: "Network sales (USD)",
      legend_send_topup: "Send Topup",
      legend_send_cash: "Send Cash",
      legend_total: "Total",
      legend_sales_usd: "Sales USD",
      legend_sent_base: "Sent (base)",
      legend_received_htg: "Received (HTG)",
    },
    // Audit Log / My Activity
    audit: {
      title: "Audit Log",
      title_activity: "My Activity",
      subtitle: "Every action taken across the platform, forever.",
      col_when: "When",
      col_actor: "Actor",
      col_action: "Action",
      col_target: "Target",
      col_ip: "IP",
      col_details: "Details",
      no_events: "No events recorded yet.",
      admin_scope: "Master Admin",
      activity_scope: "Activity",
      reload: "Reload",
      search: "Search",
      search_ph: "user, action, ref, IP…",
      from: "From",
      to: "To",
      role: "Role",
      action: "Action",
      all_roles: "All roles",
      all_actions: "All actions",
      any_status: "Any status",
      status_success: "SUCCESS",
      status_failed: "FAILED",
      clear: "Clear",
      apply: "Apply",
      col_user: "User",
      col_role: "Role",
      col_status: "Status",
      loading_trail: "Loading audit trail…",
      no_records: "No audit records match your filters.",
      records_total: "{{n}} records · page {{page}} of {{total}}",
      prev: "Prev",
      next: "Next",
      audit_entry: "Audit entry",
      when: "When",
      user: "User",
      role_label: "Role",
      target: "Target",
      status: "Status",
      reference: "Reference",
      ip: "IP",
      device_browser: "Device / Browser",
      previous: "Previous",
      new: "New",
      payload: "Payload",
      failed_load: "Failed to load audit log",
    },
    // Trusted Devices
    trusted: {
      title: "Trusted Devices",
      subtitle: "Devices that skip 2FA until you revoke them.",
      col_device: "Device",
      col_last_used: "Last used",
      col_ip: "Last IP",
      col_expires: "Expires",
      col_actions: "Actions",
      revoke: "Revoke",
      revoked: "Device revoked",
      no_devices: "No trusted devices yet.",
      reload: "Reload",
      sign_out_everywhere: "Sign out everywhere",
      mobile_devices: "Mobile devices",
      computers: "Computers",
      trust_period: "Trust period",
      days: "days",
      no_trusted_hint: "You have no trusted devices. Every new sign-in will require an SMS verification code.",
      confirm_remove: "Remove this device? You will need to verify by email the next time you sign in from it.",
      confirm_force_logout: "Revoke ALL trusted devices? Every browser will need a fresh email verification next login.",
      device_removed: "Trusted device removed",
      failed_remove: "Failed to remove",
      failed_force_logout: "Failed to force logout",
      failed_load: "Failed to load trusted devices",
      revoked_n: "Revoked {{n}} device(s)",
      added: "Added",
      last_login: "Last login",
      trust_expires_in: "Trust expires in",
      no_ip: "no ip",
      remove: "Remove",
      expired: "expired",
    },
    // Exchange Rates
    rates: {
      title: "Rates & Fees",
      subtitle: "Manage USD → local currency conversion rates.",
      col_currency: "Currency",
      col_rate: "Rate (1 USD =)",
      col_updated: "Updated",
      col_actions: "Actions",
      add: "Add currency",
      save: "Save",
      saved: "Rate saved",
      tab_tiers: "Convenience Fee Tiers",
      exchange_rates_htg: "Exchange rates → HTG",
      rates_hint: "1 unit of the listed currency = N HTG. Used as the source of truth for every Send Cash transaction.",
      reload: "Reload",
      loading_rates: "Loading rates…",
      loading_tiers: "Loading fee tiers…",
      unit_htg: "1 {{ccy}} = ? HTG",
      last_updated_by: "Last updated by {{name}}",
      no_rate_set: "No rate set — Send Cash will be blocked for this currency",
      failed_load_rates: "Failed to load exchange rates",
      failed_load_tiers: "Failed to load tiers",
      enter_valid_rate: "Enter a valid rate > 0",
      rate_updated: "{{ccy}} → HTG rate updated to {{n}}",
      save_failed: "Save failed",
      tiers_updated: "{{ccy}} fee tiers updated ({{n}})",
      tiers_hint_1: "Each row defines an amount-range ceiling. An agent can charge any convenience fee from",
      tiers_hint_zero: "0",
      tiers_hint_2: "up to the",
      tiers_hint_max_fee: "Max Fee",
      tiers_hint_3: "row that matches the transaction amount. Leave",
      tiers_hint_max_amount: "Max Amount",
      tiers_hint_4: "empty for \"no upper limit\".",
      currency_fee_tiers: "{{ccy}} convenience fee tiers",
      tier_singular: "tier",
      tier_plural: "tiers",
      add_tier: "Add tier",
      save_changes: "Save changes",
      col_min_amount: "Min Amount ({{ccy}})",
      col_max_amount: "Max Amount ({{ccy}})",
      col_max_fee: "Max Fee ({{ccy}})",
      no_tiers_for: "No tiers configured — agents cannot charge a convenience fee for {{ccy}}.",
      remove_tier: "Remove tier",
    },
    // Cash Verification
    cash_verify: {
      title: "Cash Verification",
      subtitle: "Approve pending cash-out requests.",
      col_agent: "Agent",
      col_amount: "Amount",
      col_reference: "Reference",
      col_submitted: "Submitted",
      col_actions: "Actions",
      approve: "Approve",
      reject: "Reject",
      approved: "Approved",
      rejected: "Rejected",
      no_pending: "No pending requests.",
      status_pending: "Pending",
      status_confirmed: "Confirmed",
      status_rejected: "Rejected",
      status_all: "All",
      provider: "Provider",
      status: "Status",
      back_profile: "← Profile",
      back_send_cash: "← Back to Send Cash",
      failed_load: "Failed to load",
      wallet_balance: "Wallet balance",
      commission_earned: "Commission earned",
      commission_rate_hint: "{{pct}}% rate",
      pending: "Pending",
      confirmed: "Confirmed",
      verification_users: "Verification users",
      dedicated_hint: "Two dedicated accounts that process Send Cash requests for each provider.",
      not_configured: "(not configured)",
      verifier_label: "{{provider}} verifier",
      edit: "Edit",
      create: "Create",
      balance: "Balance",
      commission: "Commission",
      earned: "Earned",
      show_only_verifier: "Show only this verifier's work →",
      showing_only_verifier: "✓ Showing only this verifier's work",
    },
    // Pending Codes
    pending_codes: {
      title: "Pending Codes",
      subtitle: "Active 2FA codes waiting to be used.",
      col_user: "User",
      col_role: "Role",
      col_code: "Code",
      col_expires: "Expires",
      no_codes: "No pending codes.",
      breadcrumb: "Security · Support",
      long_subtitle: "Active email verification codes waiting to be entered — for both Agents and Master Agents. Only visible to Master Admin and Master Agents. Every read is recorded in the Audit Log.",
      reload: "Reload",
      filter_all: "All",
      filter_masters: "Master Agents",
      filter_agents: "Agents",
      filter_verifiers: "Cash Verification Users",
      filter_moncash: "MonCash Verifier",
      filter_natcash: "NatCash Verifier",
      col_username: "Username",
      col_email: "Email",
      col_created: "Created",
      col_status: "Status",
      loading: "Loading…",
      no_active: "No active codes. When a user requests one, it will show up here for 5 minutes.",
      min_left: "{{n}} min left",
      copy_code: "Copy code",
      code_copied: "Code copied to clipboard",
      failed_load: "Failed to load pending codes",
      escalation_path: "Escalation path:",
      escalation_text: "If an agent can't receive their code, they contact their Master Agent. If the Master Agent can't resolve it, they contact the Master Admin.",
    },
    // Shared UI strings — used across pages/components.
    my_commissions: {
      title: "My Commissions",
      subtitle: "The commission percentage assigned to you per operator. This information is read-only — contact your Master Agent to request changes.",
      operators: "Operators",
      with_commission: "With commission",
      no_commission: "No commission",
      search_operators: "Search operators…",
      country: "Country",
      country_all: "All",
      only_assigned: "Only show operators with commission",
      col_id: "ID",
      col_product: "Product",
      col_country_operator: "Country / Operator",
      col_commission: "Commission",
      col_status: "Status",
      cash_badge: "cash",
      no_assigned_yet: "You do not have any commissions assigned yet. Contact your Master Agent.",
      no_match: "No operators match your filters.",
    },
    reports: {
      tab_charts: "Charts",
      tab_sales: "Sales by status",
      tab_master_agents: "By master agent",
      tab_agents: "By agent",
      tab_operators: "By operator",
      tab_countries: "By country",
      tab_commissions: "Commissions",
      tab_search: "Search transactions",
      range_all: "All time",
      range_today: "Today",
      range_yesterday: "Yesterday",
      range_7d: "Last 7 Days",
      range_30d: "Last 30 Days",
      range_month: "This Month",
      range_last_month: "Last Month",
      range_custom: "Custom Range",
      loading: "Loading…",
      no_data: "No data for this report.",
      col_status: "Status",
      col_count: "Count",
      col_total: "Total",
      col_commission: "Commission",
      col_id: "ID",
      col_master_agent: "Master agent",
      col_agents_count: "Agents",
      col_topups: "Topups",
      col_network_sales: "Network sales",
      col_commissions_paid: "Commissions paid",
      col_agent: "Agent",
      col_total_sales: "Total sales",
      col_operator: "Operator",
      col_country: "Country",
      no_data_short: "No data.",
      search_by_phone: "Search by phone number (full or partial, e.g. 5093 or 50937188551)",
      kpi_available_balance: "Available balance",
      kpi_total_sales: "Total sales",
      kpi_my_commissions: "My commissions",
      kpi_total_transactions: "Total transactions",
      showing_data: "Showing data for",
      chart_sales_earnings_month: "Sales & Earnings by Month",
      last_6_months: "Last 6 months",
      chart_sales_by_products: "Sales by Products",
      top_operators_range: "Top 6 operators · selected range",
      chart_sales_by_week: "Sales by Week",
      chart_sales_by_master: "Sales by Master Agent",
      top_8_range: "Top 8 · selected range",
      chart_payments_debits: "Payments vs Debits",
      wallet_movements: "Wallet movements in range",
      last_8_weeks: "Last 8 weeks",
      payments_in: "Payments in",
      debits_out: "Debits out",
      no_data_range: "No data available for the selected range.",
      lifetime_earnings: "Your lifetime commission earnings",
      total_earned: "Total Earned",
      no_commission_earnings: "No commission earnings yet.",
      no_transactions_for: "No transactions found for \"{{phone}}\".",
      customer_paid: "Customer paid",
      usd_internal: "USD (internal)",
      rate: "Rate",
      legend_sales: "Sales",
      legend_earnings: "Earnings",
      export_master_csv: "Master Agent CSV",
      export_agent_csv: "Agent CSV",
      export_operator_csv: "Operator CSV",
      export_country_csv: "Country CSV",
    },
    send_cash: {
      switch_to_topup: "Switch to Send Topup",
      wallets_all: "Send Cash wallets · all currencies",
      wallet_balance_label: "Send Cash balance",
      wallet_hint: "This balance is used only for Send Cash (MonCash / NatCash). It is separate from your Send Topup wallet.",
      transfer_cash_agent: "Transfer Cash to an agent",
      open_cash_verification: "Open Cash Verification",
      full_name_required: "Full Name *",
      full_name_hint: "Enter the recipient's complete name exactly as it appears on their MonCash / NatCash account.",
      recipient_phone_required: "Recipient phone *",
      haiti_prefix: "Haiti · +509",
      haiti_country_code: "Haiti country code",
      phone_hint: "Country code +509 is applied for Haiti. Type the local 8-digit number.",
      detected: "Detected",
      admin_override: "Admin override:",
      could_not_detect: "Could not auto-detect Haiti wallet. Check the phone number.",
      amount_to_send_ccy: "Amount to Send ({{ccy}}) *",
      amount_to_send_usd: "Amount to send (USD) *",
      convenience_fee_ccy: "Convenience Fee ({{ccy}})",
      convenience_fee_usd: "Convenience fee (USD)",
      max_fee: "Max {{n}} USD",
      max_fee_ccy: "Max {{n}} {{ccy}}",
      exceeds_balance: "Exceeds your balance ({{balance}})",
      live_summary: "Live summary",
      amount_to_send: "Amount to send",
      convenience_fee: "Convenience fee",
      total_to_pay: "Total to pay",
      receiver_gets: "Receiver gets",
      exchange_rate: "Exchange rate",
      note_optional_upper: "Note / reference (optional)",
      internal_reference: "Internal reference shown on receipt",
      only_amount_debited: "Only this amount is debited from your wallet.",
      profit_kept: "Your profit. Kept by you — never deducted from the wallet.",
    },
    providers: {
      breadcrumb: "Admin · Provider Management",
      title: "{{brand}} Network",
      subtitle: "Internal provider details. Visible to the Master Admin only — agents, master agents and customers never see this section.",
      master_only: "Master Admin only",
    },
    ui: {
      show: "Show",
      entries: "entries",
      of: "of",
      page: "Page",
      per_page: "{{n}} / page",
      apply: "Apply",
      prev: "Prev",
      previous: "Previous",
      next: "Next",
      showing: "Showing",
      showing_range: "Showing {{from}} – {{to}} of {{total}} {{noun}}",
      network: "Network",
      loading: "Loading…",
      // Row actions menu
      actions_btn: "Actions",
      view_sales: "View Sales",
      view_agents: "View Agents",
      edit_agent: "Edit Agent",
      edit_master_agent: "Edit master agent",
      change_password: "Change Password",
      transfer_funds_action: "Transfer Funds",
      withdraw_funds_action: "Withdraw Funds",
      transfer_cash_action: "Transfer Cash",
      withdraw_cash_action: "Withdraw Cash",
      set_commissions_action: "Set Commissions",
      // Modal titles
      create_agent_title: "Create new agent",
      edit_agent_title: "Edit agent",
      create_master_title: "Create new master agent",
      edit_master_title: "Edit master agent",
      reset_password_title: "Reset password — {{username}}",
      transfer_title: "Transfer funds → {{username}}",
      withdraw_title: "Withdraw funds — {{username}}",
      transfer_cash_title: "Transfer Cash → {{username}}",
      withdraw_cash_title: "Withdraw Cash — {{username}}",
      sales_modal_title: "Sales — {{name}} ({{code}})",
      network_sales_modal_title: "Network sales — {{name}} ({{code}})",
      // Field labels
      full_name: "Full name",
      username: "Username",
      email: "Email",
      phone_number: "Phone number",
      password: "Password",
      commission_pct: "Commission %",
      note_optional: "Note (optional)",
      internal_reference: "Internal reference",
      active_label: "Active",
      assign_master: "Assign to master agent",
      keep_under_me: "Keep under me ({{admin}})",
      new_password_min: "New password (minimum 4 chars)",
      pw_reset_hint: "The agent will need to use the new password on their next login.",
      amount_transfer_usd: "Amount to transfer (USD)",
      amount_withdraw_max: "Amount to withdraw (max {{max}})",
      amount_in: "Amount in {{currency}}",
      amount_transfer_in: "Amount to transfer in {{currency}}",
      amount_withdraw_in: "Amount to withdraw in {{currency}}",
      currency: "Currency",
      current_balance: "Current balance",
      available: "Available",
      current: "Current",
      // Info banners
      new_agents_hint: "New agents are created with $0 balance. Fund them later from Transfer Funds.",
      new_masters_hint: "New master agents start with $0 balance and no sub-agents. Fund them later via Transfer Funds.",
      cash_wallet_hint: "Send Cash wallet (separate from topup balance)",
      pull_cash_hint: "Pull Send Cash balance back to your wallet",
      agent_current_balance: "Agent's current balance",
      master_topup_balance: "Master agent's topup balance",
      funds_move_master_to_master: "Funds will move from your master admin wallet to this master agent.",
      funds_removed_agent_to_master: "Funds will be removed from this agent and credited to your master wallet.",
      funds_removed_master_to_admin: "Funds will be removed from this master agent and credited back to your Master Admin wallet.",
      // Search placeholders
      search_users: "Search ID, name, username, phone, email…",
      search_tx: "Search ID, username, phone, transaction ID…",
      // Toasts
      agent_created: "Agent created",
      agent_updated: "Agent updated",
      master_created: "Master agent created",
      master_updated: "Master agent updated",
      update_failed: "Update failed",
      create_agent_failed: "Failed to create agent",
      create_master_failed: "Failed to create master agent",
      transfer_success: "Transferred {{amount}} to {{username}}",
      withdraw_success: "Withdrew {{amount}} from {{username}}",
      cash_transfer_success: "Transferred {{amount}} {{currency}} cash to {{username}}",
      cash_withdraw_success: "Withdrew {{amount}} {{currency}} cash from {{username}}",
      transfer_failed: "Transfer failed",
      withdraw_failed: "Withdraw failed",
      cash_transfer_failed: "Transfer Cash failed",
      cash_withdraw_failed: "Withdraw Cash failed",
      pw_min_4: "Password must be at least 4 characters",
      pw_reset_for: "Password reset for {{username}}",
      pw_reset_failed: "Reset failed",
      enter_valid_amount: "Enter a valid amount",
      only_available: "Only {{amount}} {{currency}} available",
      only_master_has: "Master agent only has {{amount}} available",
      only_agent_has: "Agent only has {{amount}} available",
      agent_active: "Agent activated",
      agent_inactive: "Agent deactivated",
      master_active: "Master agent activated",
      master_inactive: "Master agent deactivated",
      could_not_load: "Could not load sales",
      failed_generic: "Failed",
      // Sub-agents transactions extra
      subagent_scope_agents: "Sub-agent transactions in the selected range.",
      subagent_scope_masters: "All master-agent transactions in the selected range.",
      export_csv: "Export CSV",
      transactions_lower: "transactions",
      agents_lower: "agents",
      master_agents_lower: "master agents",
      no_subagent_tx: "No subagent transactions in this range.",
      loading_ellipsis: "Loading…",
      // Date range
      range_today: "Today",
      range_yesterday: "Yesterday",
      range_7d: "Last 7 Days",
      range_30d: "Last 30 Days",
      range_this_month: "This Month",
      range_last_month: "Last Month",
      range_custom: "Custom Range",
      // Analytics extra
      agent_col_agent: "Agent",
      col_transactions: "Transactions",
      col_commissions: "Commissions",
      // Master filter chip
      master_agent_label: "Master agent",
      back_to_masters: "Back to Master Agents",
      clear_filter: "Clear master agent filter",
      // Provider management etc.
      no_data: "No data.",
      loading_data: "Loading data…",
      created: "Created",
      updated: "Updated",
      // Notifications
      notifications: "Notifications",
      mark_all_read: "Mark all as read",
      no_notifications: "No notifications yet.",
    },
    // Common — used everywhere. Keep this list tight and reusable.
    common: {
      loading: "Loading…",
      update: "Update",
      save: "Save",
      cancel: "Cancel",
      close: "Close",
      edit: "Edit",
      delete: "Delete",
      confirm: "Confirm",
      refresh: "Refresh",
      search: "Search",
      actions: "Actions",
      submit: "Submit",
      back: "Back",
      next: "Next",
      previous: "Previous",
      apply: "Apply",
      reset: "Reset",
      export: "Export",
      print: "Print",
      download: "Download",
      copy: "Copy",
      copied: "Copied",
      yes: "Yes",
      no: "No",
      required: "Required",
      optional: "Optional",
      status: "Status",
      date: "Date",
      amount: "Amount",
      total: "Total",
      details: "Details",
      view: "View",
      view_details: "View details",
      loading_more: "Loading more…",
      no_results: "No results found.",
      error_generic: "Something went wrong. Please try again.",
      success_saved: "Saved successfully.",
      required_field: "This field is required.",
      invalid_email: "Please enter a valid email address.",
      invalid_phone: "Please enter a valid phone number.",
      min_password: "Password must be at least 6 characters.",
    },
    change_role: {
      menu_item: "Change role / Reassign",
      section_label: "Admin control",
      title: "Change role for {{username}}",
      loading: "Loading current details…",
      current_role: "Current role",
      current_parent: "Current parent",
      wallet_balance: "USD wallet",
      history_count: "Topups · commissions",
      history_hint: "All history stays linked",
      new_role: "New role",
      new_parent_admin: "New parent (Master Admin)",
      new_parent_master: "New parent — pick a Master Admin or Master Agent",
      parent_admin: "Master Admin",
      parent_none_admin: "No parent (top of hierarchy)",
      pick_parent: "Choose the new parent…",
      impact_title: "Impact preview",
      parent_label: "Parent",
      impact_history: "Wallet balance, ledger, topups, commissions, receipts and audit are ALL preserved.",
      impact_wallet: "Wallet numbers and Send Cash balances are untouched.",
      impact_permissions: "Access permissions update at next login.",
      impact_master_perms_removed: "Master Agent permissions will be revoked. They can no longer manage agents.",
      impact_master_perms_granted: "Master Agent permissions will be granted. They can create and manage their own agents.",
      downgrade_title: "{{count}} agents currently report to this master",
      downgrade_hint: "Choose where those agents will be reassigned before we demote this user.",
      dg_move_admin: "Move all of their agents under Master Admin",
      dg_move_master: "Move all of their agents to another Master Agent",
      dg_block: "Block the change until they have no agents",
      pick_downgrade: "Please pick how to handle their sub-agents.",
      pick_downgrade_target: "Please pick the destination Master Agent.",
      admin_warn_title: "Master Admin promotion",
      admin_warn_hint: "This grants full platform control. Only do this if you truly want to give up sole admin rights.",
      admin_warn_confirm: "I understand this user will have complete admin access.",
      confirm_admin_first: "Please confirm the Master Admin promotion first.",
      reason: "Reason (optional, saved to audit log)",
      reason_placeholder: "e.g. Promoting for regional expansion",
      confirm_password: "Your admin password",
      password_placeholder: "Type your password to confirm",
      password_required: "Please confirm with your admin password.",
      apply: "Apply role change",
      saved: "Role change applied. Audit log updated.",
      failed: "Could not change role.",
      failed_preview: "Could not load user details.",
    },
    landing: {
      nav_home: "Home",
      nav_about: "About",
      nav_services: "Services",
      nav_products: "Products",
      nav_partners: "Partners",
      nav_contact: "Contact",
      cta_login: "Login",
      cta_dashboard: "Go to Dashboard",
      hero_kicker: "Instant · Secure · Global",
      hero_title: "Connect the world with {{brand}}",
      hero_subtitle: "A simple, fast and secure platform for international mobile topups, digital payments and reseller network management.",
      hero_cta_learn: "Explore services",
      hero_trust_secure: "Bank-grade encryption",
      hero_trust_instant: "Real-time settlement",
      hero_trust_multi: "Multi-role network",
      promo_text: "",
      about_kicker: "About us",
      about_title: "Designed for resellers, chosen by agents.",
      about_p1_prefix: "{{brand}} is a global recharge platform designed for ",
      about_p1_role1: "master agents",
      about_p1_between: " and ",
      about_p1_role2: "field agents",
      about_p1_suffix: ". We give you the operational backbone — wallet, ledger, receipts and compliance — so you can focus on serving your customers.",
      about_p1: "{{brand}} is a global recharge platform designed for master agents and field agents. We give you the operational backbone — wallet, ledger, receipts and compliance — so you can focus on serving your customers.",
      about_p2: "From single-country operations to multi-region reseller networks, our tools scale with you: transparent commissions, real-time analytics, and enterprise-grade auditability out of the box.",
      value_secure_title: "Secure by design",
      value_secure_desc: "2FA, trusted-device flows, audit log on every action.",
      value_realtime_title: "Real-time",
      value_realtime_desc: "Wallet, ledger and receipts settle instantly.",
      value_hierarchy_title: "Multi-role",
      value_hierarchy_desc: "Master Agent · Agent · Collaborator.",
      value_global_title: "Global",
      value_global_desc: "Multi-currency, multi-country, USD-anchored wallets.",
      value_whitelabel_title: "White Label",
      value_whitelabel_desc: "Your brand, custom design, turnkey experience for your business.",
      services_kicker: "Services",
      services_title: "Everything your reseller network needs.",
      services_sub: "Four core services — one integrated platform, one wallet, one dashboard.",
      svc_giftcards_title: "Gift Cards",
      svc_giftcards_desc: "Digital gift cards for popular brands and platforms, delivered instantly.",
      svc_topup_title: "Topup & Bill Payments",
      svc_topup_desc: "Instant mobile recharges and utility bill payments to hundreds of operators worldwide.",
      svc_intl_calls_title: "International Calls",
      svc_intl_calls_desc: "We know times are tough right now — but cost should never keep you from staying in touch with your loved ones.",
      svc_api_title: "API Integration",
      svc_api_desc: "REST API for businesses and resellers to natively integrate topups, cash-outs and gift cards.",
      products_kicker: "Products",
      products_title: "Two ways to run your business.",
      prod_web_title: "Web Portal",
      prod_web_desc: "Full-featured reseller dashboard: real-time wallet, instant topups, transparent commissions, ID-verified customers, receipts & reports.",
      prod_web_bullet_0: "Multi-role hierarchy — Master Agent · Agent · Collaborator",
      prod_web_bullet_1: "USD-only wallets with instant wallet funding",
      prod_web_bullet_2: "PDF receipts and audit-grade reports",
      prod_web_master_label: "Master Agent",
      prod_web_master_alt: "Master Agent portal preview",
      prod_web_agent_label: "Agent",
      prod_web_agent_alt: "Agent portal preview",
      prod_mobile_title: "Mobile Application",
      prod_mobile_desc: "Send topups, verify identities, approve cash-outs and monitor your agents from anywhere.",
      prod_mobile_bullet_0: "Native experience for agents on the go",
      prod_mobile_bullet_1: "Push notifications for pending approvals",
      prod_mobile_bullet_2: "Offline-safe receipts",
      prod_mobile_coming_soon: "Coming soon",
      prod_mobile_alt: "Preview of the upcoming My GlobalTopup mobile app",
      partners_kicker: "Services available",
      partners_title: "Send topups to leading operators.",
      partners_sub: "Below is a sample of the operators available through our platform. Ask our support team for the full list — hundreds of networks across dozens of countries.",
      partners_disclaimer: "All brand names, trademarks and logos mentioned are the property of their respective owners and are shown solely to identify the services available through our platform.",
      stats_title: "We speak with numbers",
      stat_operators: "Operators",
      stat_countries: "Available Countries",
      stat_users: "Users Worldwide",
      stat_support: "Customer Support",
      cta_title: "Ready to start reselling?",
      cta_sub: "Sign in to your account or contact our team to become a partner.",
      cta_whatsapp: "Chat on WhatsApp",
      cta_email_label: "Send Email",
      contact_form: {
        title: "Contact My GlobalTopup",
        subtitle: "Send us a message and our team will get back to you shortly.",
        field_name: "Full name",
        field_email: "E-mail address",
        field_phone: "Mobile phone (optional)",
        field_subject: "Subject (optional)",
        field_message: "Message",
        hint_phone: "International format, e.g. +56961234567",
        ph_name: "e.g. Jane Doe",
        ph_subject: "How can we help?",
        ph_message: "Tell us a bit about your business and how we can help.",
        err_name: "Please enter your full name.",
        err_email: "Enter a valid e-mail address.",
        err_phone: "Use international format (e.g. +56961234567).",
        err_message: "Please write a short message (5+ characters).",
        err_message_long: "Message is too long (max 2000 characters).",
        err_send: "We couldn't deliver your message. Please try again shortly.",
        cancel: "Cancel",
        send: "Send Message",
        sending: "Sending…",
        close: "Close",
        done: "Done",
        success_title: "Message sent successfully",
        success_body: "Thank you for contacting My GlobalTopup. Our team will respond as soon as possible.",
      },
      footer_tagline: "A simple, fast and secure platform for global topups and digital payments.",
      footer_company: "Company",
      footer_services: "Services",
      footer_products: "Products",
      footer_legal: "Legal",
      footer_privacy: "Privacy Policy",
      footer_terms: "Terms & Conditions",
      footer_rights: "All rights reserved.",
      footer_login: "Login",
      footer_forgot: "Forgot password",
      footer_follow: "Follow us",
      footer_download: "Download the app",
      footer_play_get: "Get it on",
      legal_back_home: "Back to home",
    },
    branding: {
      stats_section: "Landing page",
      stats_title: "Landing Page Statistics",
      stats_hint: "Numbers shown on the public homepage. Each stat is hidden until you enable it and enter a confirmed value (e.g. \"400+\", \"70+\", \"24/7\"). The entire strip can be turned off with the toggle below.",
      stats_section_on: "Section visible on homepage",
      stats_section_off: "Section hidden — no statistics will appear",
      stats_label_ph: "Label (e.g. Operators)",
      stats_value_ph: "Value (e.g. 400+, 24/7)",
      stats_shown: "Shown",
      stats_hidden: "Hidden",
      stats_saved: "Landing page statistics saved",
    },
  },
};

export default en;
